import { computed, onScopeDispose, ref, type Ref } from 'vue'
import type { PDataTableColumn, PDataTableColumnName } from '../types'

const MIN_COLUMN_WIDTH_PX = 40

const parsePxValue = (value: string | undefined | null): number | null => {
  if (!value) return null
  const match = /^(\d+(?:\.\d+)?)\s*px$/i.exec(value.trim())
  return match ? parseFloat(match[1]) : null
}

type ResizeSession = {
  name: PDataTableColumnName
  startX: number
  startWidth: number
  minWidth: number
  maxWidth: number | null
  pointerId: number
  handle: HTMLElement
}

export type ResizeSide = 'left' | 'right'

type UseColumnResizeOptions = {
  columns: Ref<readonly PDataTableColumn[]>
  enabled: Ref<boolean>
  isColumnHidden: (name: PDataTableColumnName) => boolean
}

export const useColumnResize = ({ columns, enabled, isColumnHidden }: UseColumnResizeOptions) => {
  const columnWidths = ref<Record<PDataTableColumnName, number>>({})
  const isResizing = ref<boolean>(false)
  const hasCustomColumnWidths = computed(() => Object.keys(columnWidths.value).length > 0)

  let session: ResizeSession | null = null
  let pendingFrame: number | null = null
  let latestClientX = 0

  const findColumnDef = (name: PDataTableColumnName): PDataTableColumn | undefined =>
    columns.value.find((c) => c.name === name)

  const isColumnResizable = (col: { resizable?: boolean }): boolean => {
    if (!enabled.value) return false
    return col.resizable !== false
  }

  const getPreviousResizableColumnName = (
    cols: { name: string }[],
    currentName: PDataTableColumnName,
  ): PDataTableColumnName | null => {
    const idx = cols.findIndex((c) => c.name === currentName)
    if (idx === -1) return null

    for (let i = idx - 1; i >= 0; i--) {
      const candidate = cols[i]
      if (isColumnHidden(candidate.name)) continue

      const colDef = findColumnDef(candidate.name)
      if (!colDef || !isColumnResizable(colDef)) return null

      return candidate.name
    }

    return null
  }

  const getResizeTargetName = (
    cols: { name: string }[],
    colName: PDataTableColumnName,
    side: ResizeSide,
  ): PDataTableColumnName | null => {
    const colDef = findColumnDef(colName)
    if (!colDef || !isColumnResizable(colDef)) return null

    if (side === 'right') return colName
    return getPreviousResizableColumnName(cols, colName)
  }

  const cancelPendingFrame = (): void => {
    if (pendingFrame !== null) {
      cancelAnimationFrame(pendingFrame)
      pendingFrame = null
    }
  }

  const applyResize = (): void => {
    pendingFrame = null
    if (!session) return

    const minDelta = session.minWidth - session.startWidth
    const maxDelta = session.maxWidth !== null ? session.maxWidth - session.startWidth : Infinity

    const delta = Math.min(Math.max(latestClientX - session.startX, minDelta), maxDelta)

    columnWidths.value = {
      ...columnWidths.value,
      [session.name]: Math.round(session.startWidth + delta),
    }
  }

  const onPointerMove = (event: PointerEvent): void => {
    if (!session || event.pointerId !== session.pointerId) return

    latestClientX = event.clientX
    if (pendingFrame !== null) return
    pendingFrame = requestAnimationFrame(applyResize)
  }

  const stopResize = (event?: PointerEvent): void => {
    if (!session) return
    if (event && event.pointerId !== session.pointerId) return

    const { handle, pointerId } = session

    handle.removeEventListener('pointermove', onPointerMove)
    handle.removeEventListener('pointerup', stopResize)
    handle.removeEventListener('pointercancel', stopResize)

    if (handle.hasPointerCapture?.(pointerId)) {
      handle.releasePointerCapture(pointerId)
    }

    cancelPendingFrame()

    session = null
    isResizing.value = false
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }

  const freezeOtherColumnWidths = (headerRow: HTMLElement): void => {
    const captured: Record<string, number> = { ...columnWidths.value }
    headerRow.querySelectorAll<HTMLElement>('th[data-column-name]').forEach((el) => {
      const name = el.dataset.columnName
      if (name && !(name in captured)) {
        captured[name] = Math.round(el.getBoundingClientRect().width)
      }
    })
    columnWidths.value = captured
  }

  const startColumnResize = (event: PointerEvent, colName: PDataTableColumnName): void => {
    event.preventDefault()
    event.stopPropagation()

    const handle = event.currentTarget as HTMLElement | null
    const headerRow = handle?.closest('tr') as HTMLElement | null
    if (!handle || !headerRow) return

    const th = headerRow.querySelector<HTMLElement>(
      `th[data-column-name="${CSS.escape(String(colName))}"]`,
    )
    if (!th) return

    const colDef = findColumnDef(colName)
    if (!colDef) return

    freezeOtherColumnWidths(headerRow)

    session = {
      name: colName,
      startX: event.clientX,
      startWidth: th.getBoundingClientRect().width,
      minWidth: parsePxValue(colDef.minWidth) ?? MIN_COLUMN_WIDTH_PX,
      maxWidth: parsePxValue(colDef.maxWidth),
      pointerId: event.pointerId,
      handle,
    }
    latestClientX = event.clientX
    isResizing.value = true
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'

    handle.setPointerCapture(event.pointerId)
    handle.addEventListener('pointermove', onPointerMove)
    handle.addEventListener('pointerup', stopResize)
    handle.addEventListener('pointercancel', stopResize)
  }

  const getColumnWidthStyle = (column: PDataTableColumn): Record<string, string> => {
    const resized = columnWidths.value[column.name]
    if (typeof resized === 'number') {
      const value = `${resized}px`
      return { width: value, 'min-width': value, 'max-width': value }
    }

    const style: Record<string, string> = {}
    if (column.width) style['width'] = column.width
    if (column.minWidth) style['min-width'] = column.minWidth
    if (column.maxWidth) style['max-width'] = column.maxWidth
    return style
  }

  onScopeDispose(() => {
    cancelPendingFrame()
    if (session) stopResize()
  })

  return {
    columnWidths,
    isResizing,
    hasCustomColumnWidths,
    isColumnResizable,
    getResizeTargetName,
    startColumnResize,
    getColumnWidthStyle,
  }
}
